import "./Print.scss";
import {
    Typography,
    Stack,
    Divider,
    CircularProgress,
    Grid,
    Button,
    Select,
    Slider,
} from "@mui/material";
import { fromUnixTime } from "date-fns";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import useSWRImmutable from "swr/immutable";

import { RowChoice, RowBase, RowChoiceMeta } from "./Rows/RowBase";
import { use404Page } from "../../../hooks/use404Page";
import { CompanyMetadata } from "../../../models/metadata/Metadata";
import { useWorkOrderViewService } from "../../../service/WorkOrderService";

interface WorkOrderPrintContentProps {
    workOrderId: number;
}

function WorkOrderPrintContent({ workOrderId }: WorkOrderPrintContentProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [metadata, setMetadata] = useState<RowChoiceMeta>({
        imageColumns: 3,
    });

    const { workOrderResponse } = useWorkOrderViewService(workOrderId, false);

    const metadataResult = useSWRImmutable<CompanyMetadata>("metadata");

    const isReady =
        metadataResult.data !== undefined &&
        !metadataResult.isLoading &&
        workOrderResponse.data !== undefined &&
        !workOrderResponse.isLoading;

    useEffect(() => {
        if (isReady) {
            // window.print();
        }
    }, [isReady]);

    if (!isReady) {
        return <CircularProgress />;
    }

    document.documentElement.style.setProperty(
        "--theme",
        metadataResult.data?.themeColor ?? null,
    );

    return (
        <div className="printer-page-root">
            <div className="layout-container">
                <div className="sheet-outer">
                    <Grid container spacing={2} style={{ marginBottom: "2em" }}>
                        <Grid item xs={7}>
                            <table className="company-info">
                                <tbody>
                                    <tr>
                                        <td>
                                            <Typography>
                                                Opdrachtnemer:
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography>
                                                {metadataResult.data?.name}
                                            </Typography>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <Typography>
                                                Opdrachtgever:
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography>
                                                {`${workOrderResponse.data?.customerName} - ${workOrderResponse.data?.customerLocationName}`}
                                            </Typography>
                                            <Typography>
                                                {
                                                    workOrderResponse.data
                                                        ?.customerLocationAddress
                                                }
                                            </Typography>
                                            <Typography>
                                                {`${workOrderResponse.data?.customerLocationPostal} ${workOrderResponse.data?.customerLocationResidence}`}
                                            </Typography>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </Grid>
                        <Grid item xs={5}>
                            <img
                                src={`${window.location.origin}/api/metadata/logo?variant=SMALL`}
                                alt="Bedrijf logo"
                                style={{
                                    width: "100%",
                                }}
                            />
                        </Grid>
                    </Grid>

                    <Typography variant="h5">
                        {`${t("pages.workorder.header_view")} - ${
                            workOrderResponse.data?.workListName
                        }`}
                    </Typography>
                    <Typography>Omschrijving locatie:</Typography>
                    <Typography>
                        {workOrderResponse.data?.customerLocationDescription}
                    </Typography>
                    <Typography>Reden van inschakeling:</Typography>
                    <Typography>
                        {
                            workOrderResponse.data
                                ?.customerLocationEmploymentReason
                        }
                    </Typography>

                    <div className="divider" style={{ marginTop: "1em" }} />

                    <Stack gap={3} sx={{ marginTop: 3 }}>
                        {workOrderResponse.data?.answers.map((answer, idx) => (
                            <Fragment key={`workorder_row_${answer.id}`}>
                                {answer.question !== undefined && idx > 0 && (
                                    <Divider flexItem />
                                )}
                                <RowChoice
                                    answer={answer}
                                    controlPoints={
                                        workOrderResponse.data?.controlPoints ??
                                        []
                                    }
                                    meta={metadata}
                                />
                            </Fragment>
                        ))}
                    </Stack>

                    <div className="pagebreak" />

                    <Stack gap={3}>
                        <RowBase question="Werkzaamheden verricht door">
                            <Typography>
                                {workOrderResponse.data?.performedBy}
                            </Typography>
                        </RowBase>
                        <RowBase question="Bezoekdatum">
                            <Typography>
                                {workOrderResponse.data
                                    ? fromUnixTime(
                                          workOrderResponse.data.visitationDate,
                                      ).toLocaleDateString(undefined, {
                                          dateStyle: "full",
                                      })
                                    : "-"}
                            </Typography>
                        </RowBase>
                        <RowBase question="Handtekening">
                            <img
                                src={`data:image/png;base64, ${workOrderResponse.data?.signature}`}
                                alt="Werkbon handtekening"
                                style={{
                                    border: "1px solid black",
                                    boxSizing: "border-box",
                                    width: "100%",
                                }}
                            />
                        </RowBase>
                        <RowBase question="Ondertekend door">
                            <Typography>
                                {workOrderResponse.data?.signedBy}
                            </Typography>
                        </RowBase>
                        <RowBase question="Opmerkingen">
                            <Typography>
                                {workOrderResponse.data?.comment ?? "-"}
                            </Typography>
                        </RowBase>
                    </Stack>
                </div>
                <div className="explanation-dialog">
                    <Typography variant="h5" noWrap component="h1">
                        Opslaan van het document
                    </Typography>
                    <Typography>
                        Om het document op te kunnen slaan als PDF kunt u op de
                        knop hieronder drukken om het print venster te openen.
                    </Typography>
                    <Typography>
                        U kunt in het venster bij de <b>bestemming</b> de optie{" "}
                        <b>Opslaan als PDF</b> kiezen.
                    </Typography>
                    <Typography variant="h6" noWrap component="h1">
                        Opties
                    </Typography>
                    <Typography>Aantal kolommen voor foto's:</Typography>
                    <Slider
                        getAriaValueText={value => value.toString()}
                        step={1}
                        min={1}
                        max={6}
                        valueLabelDisplay="on"
                        value={metadata.imageColumns}
                        onChange={(_ev, value) =>
                            setMetadata(old => ({
                                ...old,
                                imageColumns: value as number,
                            }))
                        }
                    />
                    <Button variant="contained" onClick={window.print}>
                        Open print venster
                    </Button>
                    <Button
                        color="inherit"
                        variant="contained"
                        onClick={() => navigate(-1)}
                    >
                        Terug naar vorige pagina
                    </Button>
                </div>
            </div>
        </div>
    );
}

function WorkOrderPrintErrorRedirect() {
    use404Page(true);
    return <></>;
}

export function WorkOrderPrintPage() {
    const { workOrderId } = useParams();

    let workOrderIdParsed: number | undefined = undefined;
    if (workOrderId) {
        workOrderIdParsed = parseInt(workOrderId, 10);
        if (isNaN(workOrderIdParsed)) {
            workOrderIdParsed = undefined;
        }
    }

    if (workOrderIdParsed === undefined) {
        return <WorkOrderPrintErrorRedirect />;
    } else {
        return <WorkOrderPrintContent workOrderId={workOrderIdParsed} />;
    }
}
